"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, ArrowRight, Loader2, Sparkles, Building2, Globe, Smartphone, Database, Cpu, HelpCircle, MessageCircle } from "lucide-react";

export type DiscoveryCategory = "website" | "mobile" | "crm" | "automation" | "custom" | "notsure";

interface ContextualDiscoveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCategory?: DiscoveryCategory;
}

const CATEGORY_CONFIG: Record<DiscoveryCategory, {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  optionsLabel: string;
  options: string[];
  timelineOptions: string[];
}> = {
  website: {
    title: "Website & Web Application Discovery",
    subtitle: "Tell us about the digital experience or web portal you want to engineer.",
    icon: Globe,
    optionsLabel: "What type of web solution do you need?",
    options: ["Corporate / Business Website", "High-Performance E-Commerce", "Customer / Client Portal", "SaaS Web Application", "Custom Web Platform"],
    timelineOptions: ["Within 30 Days", "1 - 2 Months", "2 - 4 Months", "Flexible"],
  },
  mobile: {
    title: "Mobile Application Discovery",
    subtitle: "Native iOS, Android, and cross-platform mobile apps built for scale.",
    icon: Smartphone,
    optionsLabel: "What type of mobile app are you planning?",
    options: ["Customer-Facing Mobile App", "Internal Employee / Operations App", "On-Demand Delivery / Booking App", "Marketplace / Platform", "Bespoke Cross-Platform App"],
    timelineOptions: ["Within 45 Days", "2 - 3 Months", "3 - 6 Months", "Flexible"],
  },
  crm: {
    title: "CRM & ERP Systems Architecture",
    subtitle: "Eliminate per-seat SaaS taxes with a custom operational pipeline.",
    icon: Database,
    optionsLabel: "Which core workflows need to be unified?",
    options: ["WhatsApp Automated Lead Qualification", "Custom Sales Pipeline & Kanban", "Multi-Branch Inventory & Invoicing", "Client Portal & Payment Processing", "Full Operational ERP Suite"],
    timelineOptions: ["Immediate (2 - 4 Weeks)", "1 - 2 Months", "Quarterly Rollout", "Exploring Options"],
  },
  automation: {
    title: "AI & Workflow Automation Control",
    subtitle: "Autonomous event-driven engines to eliminate manual bottlenecks.",
    icon: Cpu,
    optionsLabel: "What manual process is slowing down your team?",
    options: ["Inbound Lead Response & Triage", "Document OCR & Data Extraction", "Cross-Platform Webhook Sync", "Automated WhatsApp Customer Re-engagement", "Bespoke AI Decision Agents"],
    timelineOptions: ["ASAP", "Within 1 Month", "2 - 3 Months", "Ongoing Strategy"],
  },
  custom: {
    title: "Custom Enterprise Software",
    subtitle: "Bespoke architectures engineered specifically for your business logic.",
    icon: Building2,
    optionsLabel: "What represents your primary objective?",
    options: ["Modernize Legacy Database / System", "Build New Revenue-Generating Product", "Automate Multi-Department Operations", "API & Cloud Infrastructure Scale", "Complete Digital Transformation"],
    timelineOptions: ["1 - 3 Months", "3 - 6 Months", "Enterprise Roadmap", "Flexible"],
  },
  notsure: {
    title: "Project Discovery & Feasibility Session",
    subtitle: "Not sure where to begin? We will audit your workflow and design the optimal roadmap.",
    icon: HelpCircle,
    optionsLabel: "What is your biggest operational headache?",
    options: ["Leads are falling through the cracks", "Too many manual spreadsheets & tools", "Current software is slow or expensive", "Need custom functionality SaaS can't offer", "Looking to build a new software idea"],
    timelineOptions: ["Ready to start immediately", "Planning for next month", "Gathering technical feasibility", "Budget allocation stage"],
  },
};

export const ContextualDiscoveryModal: React.FC<ContextualDiscoveryModalProps> = ({
  isOpen,
  onClose,
  initialCategory = "website",
}) => {
  const [category, setCategory] = useState<DiscoveryCategory>(initialCategory);
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedTimeline, setSelectedTimeline] = useState<string>("");
  const [budgetRange, setBudgetRange] = useState<string>("₹2.5L - ₹5L ($3k - $6k)");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    requirements: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setCategory(initialCategory);
      setSelectedType("");
      setSelectedTimeline("");
      setIsSubmitted(false);
    }
  }, [isOpen, initialCategory]);

  const currentConfig = CATEGORY_CONFIG[category];
  const IconComponent = currentConfig.icon;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: `Discovery: ${currentConfig.title}`,
        budget: budgetRange,
        timeline: selectedTimeline || "Flexible",
        message: `[Category: ${category.toUpperCase()}]\nSelected Sub-Type: ${selectedType || "Not specified"}\n\nClient Notes:\n${formData.requirements}`,
        source: "Contextual Storytelling Discovery Modal",
      };

      await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setIsSubmitted(true);
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto overscroll-contain">
        {/* Light backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-[#111318]/50 backdrop-blur-sm"
        />

        {/* Clean White Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 15 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-2xl bg-white border border-[#111318]/10 rounded-3xl shadow-2xl overflow-hidden z-10 my-8 text-[#111318]"
        >
          {/* Header Bar */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-[#111318]/10 bg-[#F8F8F6]">
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-white border border-[#111318]/10 flex items-center justify-center text-[#2563EB] shadow-sm">
                <IconComponent className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#111318] tracking-tight">{currentConfig.title}</h3>
                <p className="text-xs text-[#5E636B]">{currentConfig.subtitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-[#5E636B] hover:text-[#111318] hover:bg-[#F1F2F3] transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {isSubmitted ? (
            <div className="p-8 sm:p-12 text-center flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 mb-5">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold text-[#111318] mb-2">Discovery Plan Initiated</h4>
              <p className="text-sm text-[#5E636B] max-w-md mb-6 leading-relaxed">
                Thank you, {formData.name || "partner"}. Our senior engineering lead has received your project parameters and will reach out with a direct architectural blueprint within 4 business hours.
              </p>
              <div className="p-4 rounded-xl bg-[#F8F8F6] border border-[#111318]/10 text-xs text-[#5E636B] text-left w-full max-w-md mb-6 space-y-1.5 font-mono">
                <div><span className="text-[#111318] font-bold">CATEGORY:</span> {currentConfig.title}</div>
                <div><span className="text-[#111318] font-bold">SCOPE:</span> {selectedType || "Custom Architecture"}</div>
                <div><span className="text-[#111318] font-bold">TIMELINE:</span> {selectedTimeline || "Flexible"}</div>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <a
                  href={`https://wa.me/919880893416?text=${encodeURIComponent(`Hi Johnson Softwares, I just submitted a ${currentConfig.title} discovery inquiry on your website.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-2 shadow-sm transition-all"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Chat on WhatsApp</span>
                </a>
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 rounded-xl bg-[#F1F2F3] hover:bg-[#E5E7EB] text-[#111318] text-xs font-semibold transition-all"
                >
                  Return to Virtual Tour
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Category Quick Selector */}
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-[#5E636B] mb-2.5 font-semibold">
                  Select Department Stream
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {(["website", "mobile", "crm", "automation", "custom", "notsure"] as DiscoveryCategory[]).map((cat) => {
                    const active = category === cat;
                    const labels: Record<DiscoveryCategory, string> = {
                      website: "Web",
                      mobile: "Mobile",
                      crm: "CRM / ERP",
                      automation: "AI / Auto",
                      custom: "Custom",
                      notsure: "Unsure",
                    };
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => {
                          setCategory(cat);
                          setSelectedType("");
                        }}
                        className={`py-2 px-1 text-xs font-semibold rounded-lg border transition-all text-center ${
                          active
                            ? "bg-[#2563EB] border-[#2563EB] text-white shadow-sm"
                            : "bg-[#F8F8F6] border-[#111318]/10 text-[#5E636B] hover:text-[#111318] hover:bg-[#F1F2F3]"
                        }`}
                      >
                        {labels[cat]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 1: Solution Options */}
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-[#5E636B] mb-2.5 font-semibold">
                  {currentConfig.optionsLabel}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {currentConfig.options.map((opt) => {
                    const isSelected = selectedType === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setSelectedType(opt)}
                        className={`p-3 text-left rounded-xl border text-xs sm:text-sm font-medium transition-all flex items-center justify-between ${
                          isSelected
                            ? "bg-[#2563EB]/10 border-[#2563EB] text-[#111318]"
                            : "bg-[#F8F8F6] border-[#111318]/5 text-[#5E636B] hover:text-[#111318] hover:bg-[#F1F2F3]"
                        }`}
                      >
                        <span>{opt}</span>
                        {isSelected && <CheckCircle2 className="w-4 h-4 text-[#2563EB] shrink-0 ml-2" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2: Timeline & Budget */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-[#5E636B] mb-2 font-semibold">
                    Target Deployment Window
                  </label>
                  <select
                    value={selectedTimeline}
                    onChange={(e) => setSelectedTimeline(e.target.value)}
                    className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                  >
                    <option value="">Select target timeline...</option>
                    {currentConfig.timelineOptions.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-[#5E636B] mb-2 font-semibold">
                    Estimated Budget Tier
                  </label>
                  <select
                    value={budgetRange}
                    onChange={(e) => setBudgetRange(e.target.value)}
                    className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                  >
                    <option value="₹1L - ₹2.5L ($1.5k - $3k)">Starter Architecture (₹1L - ₹2.5L)</option>
                    <option value="₹2.5L - ₹5L ($3k - $6k)">Growth System (₹2.5L - ₹5L)</option>
                    <option value="₹5L - ₹12L ($6k - $15k)">Enterprise Scale (₹5L - ₹12L)</option>
                    <option value="₹12L+ ($15k+) ">Custom Distributed Platform (₹12L+)</option>
                  </select>
                </div>
              </div>

              {/* Step 3: Contact & Project Notes */}
              <div className="space-y-3 pt-2 border-t border-[#111318]/10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[#5E636B] mb-1">Your Name *</label>
                    <input
                      required
                      type="text"
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-3.5 py-2 text-xs sm:text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#5E636B] mb-1">Company / Organization *</label>
                    <input
                      required
                      type="text"
                      placeholder="e.g. Apex Enterprises"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-3.5 py-2 text-xs sm:text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[#5E636B] mb-1">Work Email *</label>
                    <input
                      required
                      type="email"
                      placeholder="rahul@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-3.5 py-2 text-xs sm:text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#5E636B] mb-1">Phone / WhatsApp *</label>
                    <input
                      required
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-3.5 py-2 text-xs sm:text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#5E636B] mb-1">Core Problem or Specific Features Needed</label>
                  <textarea
                    rows={2}
                    placeholder="Briefly describe what challenges your current system faces..."
                    value={formData.requirements}
                    onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                    className="w-full bg-[#F8F8F6] border border-[#111318]/10 rounded-xl px-3.5 py-2 text-xs sm:text-sm text-[#111318] focus:outline-none focus:border-[#2563EB] focus:bg-white resize-none"
                  />
                </div>
              </div>

              {/* Submit CTA */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-[#5E636B]">
                  <Sparkles className="w-4 h-4 text-[#2563EB]" />
                  <span>Includes 100% IP ownership &amp; zero per-user SaaS license lock-in.</span>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-7 py-3 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white text-sm font-semibold transition-all shadow-[0_4px_14px_rgba(37,99,235,0.25)] flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Transmitting Blueprint...</span>
                    </>
                  ) : (
                    <>
                      <span>Dispatch Technical Inquiry</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
